import { type Context, createContext, type ReactNode, useContext } from "react";
import { OrganizationMemberClientAccessSource } from "@common/types/organizationMemberClientAccess";
import { useScope } from "@state/scopeContext.lib";
import type { ClientsWorkOverviewWorkRow } from "../clientsWorkOverviewViewConfig";
import {
    type ClientsWorkOverviewCallbacks,
    useWorkRowActions,
} from "../WorkRowActions";

/** What the Work list's row actions read, once per table. */
export interface WorkListActions {
    callbacks: ClientsWorkOverviewCallbacks;
    onConfirmationRequest: ReturnType<
        typeof useWorkRowActions
    >["onConfirmationRequest"];
    onOpenWorkspace: (workspaceId: string) => void;
}

const WorkListActionsContext: Context<WorkListActions | null> =
    createContext<WorkListActions | null>(null);

/** The row actions' shared values; `null` outside a `WorkListActionsProvider`. */
export const useWorkListActions = () => useContext(WorkListActionsContext);

interface WorkListActionsProviderProps {
    callbacks: ClientsWorkOverviewCallbacks;
    /** The table's own open: it remembers which copy of the work was opened. */
    onOpenDetails: (row: ClientsWorkOverviewWorkRow) => void;
    children: ReactNode;
}

/**
 * Runs the Work list's row actions once for the whole table: every row's
 * actions cell reads this context, and the one Cancel or Delete confirmation
 * renders here.
 */
export const WorkListActionsProvider = ({
    callbacks,
    onOpenDetails,
    children,
}: Readonly<WorkListActionsProviderProps>) => {
    const { onConfirmationRequest, confirmationModal } = useWorkRowActions();
    const { changeActiveWorkspace } = useScope();
    const value: WorkListActions = {
        callbacks: { ...callbacks, onOpenDetails },
        onConfirmationRequest,
        onOpenWorkspace: (workspaceId) =>
            changeActiveWorkspace(
                workspaceId,
                OrganizationMemberClientAccessSource.CLIENTS_LIST,
            ),
    };

    return (
        <WorkListActionsContext.Provider value={value}>
            {children}
            {confirmationModal}
        </WorkListActionsContext.Provider>
    );
};
